import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Trophy, TrendingUp, TrendingDown, Users, Star, AlertTriangle, RefreshCw, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, PieChart, Pie } from "recharts";
import { toast } from "sonner";

const tierColors = {
  "Elite VIP": "#1A1A1A", "VIP": "#8B5CF6", "Loyal Core": "#2D5936",
  "Opportunity": "#274F7D", "At Risk": "#805B10", "Needs Attention": "#8A2A23"
};

export default function ClientScoreDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [allScores, setAllScores] = useState([]);
  const [filterTier, setFilterTier] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [dashRes, scoresRes] = await Promise.all([
        api.get("/score-dashboard"),
        api.get("/client-scores"),
      ]);
      setDashboard(dashRes.data);
      setAllScores(scoresRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      await api.post("/client-scores/refresh-all");
      await fetchData();
      toast.success("All scores refreshed");
    } catch (err) { toast.error("Failed"); }
    finally { setRefreshing(false); }
  };

  const filtered = filterTier === "all" ? allScores : allScores.filter(s => s.tier_badge === filterTier);

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;

  const d = dashboard || {};

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="client-score-dashboard">
      <div className="flex items-center justify-between mb-8 animate-fadeIn">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-slate-900">Client Scores</h1>
          <p className="text-slate-600 text-sm mt-1">Client value intelligence and retention insights</p>
        </div>
        <Button data-testid="refresh-scores-btn" onClick={refreshAll} disabled={refreshing} variant="outline"
          className="border-slate-200 rounded-full h-10 px-5 text-sm">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? "Calculating..." : "Recalculate All"}
        </Button>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3"><Trophy className="w-5 h-5 text-rose-400" strokeWidth={1.5} /></div>
          <p className="font-['Outfit'] text-4xl font-medium text-slate-900">{d.avg_score || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Average Score</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3"><Users className="w-5 h-5 text-slate-400" strokeWidth={1.5} /></div>
          <p className="font-['Outfit'] text-4xl font-medium text-slate-900">{d.total_clients || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Scored Clients</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3"><Star className="w-5 h-5 text-[#8B5CF6]" strokeWidth={1.5} /></div>
          <p className="font-['Outfit'] text-4xl font-medium text-[#8B5CF6]">
            {(d.tier_distribution || []).filter(t => t.name === "Elite VIP" || t.name === "VIP").reduce((s, t) => s + t.count, 0)}
          </p>
          <p className="text-sm text-slate-400 mt-1">VIP Clients</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-5 h-5 text-amber-600" strokeWidth={1.5} /></div>
          <p className="font-['Outfit'] text-4xl font-medium text-amber-600">
            {(d.at_risk_vips || []).length}
          </p>
          <p className="text-sm text-slate-400 mt-1">At-Risk VIPs</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Tier Distribution */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="tier-distribution">
          <h3 className="font-['Outfit'] text-lg font-medium text-slate-900 mb-4">Client Tiers</h3>
          <div className="flex gap-6">
            <div className="w-1/2 h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={(d.tier_distribution || []).filter(t => t.count > 0)} dataKey="count" nameKey="name"
                    cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3}>
                    {(d.tier_distribution || []).filter(t => t.count > 0).map((t, i) => (
                      <Cell key={i} fill={t.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2 justify-center flex flex-col">
              {(d.tier_distribution || []).map(t => (
                <div key={t.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                    <span className="text-sm text-slate-600">{t.name}</span>
                  </div>
                  <span className="text-sm font-medium text-slate-900">{t.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Clients */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="top-clients">
          <h3 className="font-['Outfit'] text-lg font-medium text-slate-900 mb-4">Top Clients</h3>
          <div className="space-y-2.5">
            {(d.top_clients || []).slice(0, 8).map((c, i) => (
              <div key={c.client_id} className="flex items-center justify-between py-1.5 cursor-pointer hover:bg-[#FAFAFA] -mx-2 px-2 rounded-lg transition-colors"
                onClick={() => navigate(`/clients/${c.client_id}`)}>
                <div className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: c.tier_color }}>
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium text-slate-900">{c.client_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${c.tier_color}15`, color: c.tier_color }}>{c.tier}</span>
                  <span className="font-['Outfit'] text-base font-medium text-slate-900">{c.score}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Insights Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {(d.at_risk_vips || []).length > 0 && (
          <div className="bg-[#FDF3E1] border border-[#F3D8A2] rounded-2xl p-6" data-testid="at-risk-section">
            <h3 className="font-['Outfit'] text-lg font-medium text-amber-600 mb-3">At-Risk High-Value Clients</h3>
            <p className="text-sm text-amber-600/70 mb-4">These clients have significant lifetime value but declining scores</p>
            {(d.at_risk_vips || []).map(c => (
              <div key={c.client_id} className="flex items-center justify-between py-2 border-b border-[#F3D8A2]/40 last:border-0 cursor-pointer"
                onClick={() => navigate(`/clients/${c.client_id}`)}>
                <span className="text-sm font-medium text-amber-600">{c.client_name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-amber-600/70">LTV: ${c.ltv?.toFixed(0)}</span>
                  <span className="font-medium text-amber-600">Score: {c.score}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {(d.no_reviews_high_value || []).length > 0 && (
          <div className="bg-[#EAF0F6] border border-[#C1D4E8] rounded-2xl p-6" data-testid="no-reviews-section">
            <h3 className="font-['Outfit'] text-lg font-medium text-blue-600 mb-3">High-Value, No Reviews</h3>
            <p className="text-sm text-blue-600/70 mb-4">These strong clients haven't left any reviews — ask them!</p>
            {(d.no_reviews_high_value || []).map(c => (
              <div key={c.client_id} className="flex items-center justify-between py-2 border-b border-[#C1D4E8]/40 last:border-0 cursor-pointer"
                onClick={() => navigate(`/clients/${c.client_id}`)}>
                <span className="text-sm font-medium text-blue-600">{c.client_name}</span>
                <span className="font-medium text-blue-600">Score: {c.score}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* All Clients Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-['Outfit'] text-base font-medium text-slate-900">All Client Scores</h3>
          <Select value={filterTier} onValueChange={setFilterTier}>
            <SelectTrigger data-testid="tier-filter" className="w-[160px] h-8 border-slate-200 rounded-lg text-sm"><SelectValue placeholder="All tiers" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tiers</SelectItem>
              <SelectItem value="elite">Elite VIP</SelectItem>
              <SelectItem value="vip">VIP</SelectItem>
              <SelectItem value="loyal">Loyal Core</SelectItem>
              <SelectItem value="opportunity">Opportunity</SelectItem>
              <SelectItem value="at-risk">At Risk</SelectItem>
              <SelectItem value="low">Needs Attention</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <table className="w-full">
          <thead>
            <tr className="bg-[#FAFAFA] border-b border-slate-200">
              <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Client</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Score</th>
              <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Tier</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Spend</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Rebook</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Retail</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Reviews</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Attend</th>
              <th className="text-center px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Recency</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => {
              const b = s.breakdown || {};
              return (
                <tr key={s.client_id} className="border-b border-slate-200 hover:bg-[#FAFAFA] cursor-pointer transition-colors"
                  data-testid={`score-row-${s.client_id}`} onClick={() => navigate(`/clients/${s.client_id}`)}>
                  <td className="px-6 py-3.5">
                    <p className="text-sm font-medium text-slate-900">{s.client_name}</p>
                    <p className="text-xs text-slate-400">{s.email}</p>
                  </td>
                  <td className="px-3 py-3.5 text-center">
                    <span className="font-['Outfit'] text-lg font-semibold" style={{ color: s.tier_color }}>{s.score}</span>
                  </td>
                  <td className="px-3 py-3.5">
                    <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: `${s.tier_color}15`, color: s.tier_color }}>{s.tier}</span>
                  </td>
                  {["total_spend", "rebooking", "product_purchases", "reviews", "attendance", "recency"].map(cat => (
                    <td key={cat} className="px-3 py-3.5 text-center">
                      <div className="flex items-center justify-center">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold"
                          style={{
                            backgroundColor: (b[cat]?.score || 0) >= 60 ? '#E8F0E9' : (b[cat]?.score || 0) >= 30 ? '#FDF3E1' : '#FBEAE9',
                            color: (b[cat]?.score || 0) >= 60 ? '#2D5936' : (b[cat]?.score || 0) >= 30 ? '#805B10' : '#8A2A23'
                          }}>
                          {b[cat]?.score || 0}
                        </div>
                      </div>
                    </td>
                  ))}
                  <td className="px-3 py-3.5"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
